import { CancelTokenSource } from 'axios';
import { DateTime } from 'luxon';

import { Filter, FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models';
import { AudienceConcentrationPerUnitData } from '@/common/models/audience/AudienceConcentrationPerUnit';
import { toServerDate } from '@/globals';
import rootStore from '@/store';

type FilterComparisonValue = (typeof FilterComparisonOperator)[keyof typeof FilterComparisonOperator];
type BatchedConcentrationParams = {
  unitIDs: number[];
  demographicIDs: number[];
  queryParams?: QueryParams;
  showLoader?: boolean;
  cancelTokenSource?: CancelTokenSource;
};
type FilteredBatchedConcentrationParams = Omit<BatchedConcentrationParams, 'queryParams'> & {
  value: number;
  filter: FilterComparisonValue;
};
export type UnitAudienceConcentrationData = AudienceConcentrationPerUnitData & { unitID: number };

const BATCH_UNIT_COUNT = 50000;

async function getUnbatchedConcentrationData(
  unitIDs: number[],
  demographicIDs: number[],
  queryParams: QueryParams,
  showLoader: boolean,
  cancelTokenSource: CancelTokenSource,
) {
  const params = queryParams.toObject();
  const data = {
    unit_ids: unitIDs,
    demographic_ids: demographicIDs,
    date_from: toServerDate(DateTime.local().startOf('week')),
    date_to: toServerDate(DateTime.local().endOf('week').startOf('day')),
  };
  const result = await rootStore.dispatch('orgApp/getDailyAndAverageConcentrationPerUnit', {
    params,
    data,
    cancelTokenSource,
    showLoader,
  });
  if (result) {
    return {
      count: result.totalCount,
      data: result.data.data,
    };
  }
  // Cancelled / error
  return { count: 0, data: null };
}

async function getBatchedConcentrationDataFiltered({
  unitIDs = [],
  demographicIDs = [],
  value,
  showLoader = false,
  cancelTokenSource = null,
  filter,
}: FilteredBatchedConcentrationParams): Promise<{ count: number; data: UnitAudienceConcentrationData[] }> {
  const queryParams = QueryParams.for({
    top: unitIDs.length,
    filterCollection: new FilterCollection([new Filter('average_concentrations', [value], filter)]),
  });
  return await getBatchedConcentrationData({
    unitIDs,
    demographicIDs,
    queryParams,
    showLoader,
    cancelTokenSource,
  });
}

async function getBatchedConcentrationData({
  unitIDs = [],
  demographicIDs = [],
  queryParams = new QueryParams(unitIDs.length),
  showLoader = false,
  cancelTokenSource = null,
}: BatchedConcentrationParams): Promise<{ count: number; data: UnitAudienceConcentrationData[] }> {
  // Dont batch if we don't have to
  if (unitIDs.length <= BATCH_UNIT_COUNT)
    return await getUnbatchedConcentrationData(unitIDs, demographicIDs, queryParams, showLoader, cancelTokenSource);
  // Batching time
  const promises = [];
  const { $skip: skip, $top: top, ...params } = queryParams.toObject();
  for (let i = 0; i < unitIDs.length; i += BATCH_UNIT_COUNT) {
    const j = Math.min(unitIDs.length, i + BATCH_UNIT_COUNT);
    const units = unitIDs.slice(i, j);
    const data = {
      unit_ids: units,
      demographic_ids: demographicIDs,
      date_from: toServerDate(DateTime.local().startOf('week')),
      date_to: toServerDate(DateTime.local().endOf('week').startOf('day')),
    };
    promises.push(
      rootStore.dispatch('orgApp/getDailyAndAverageConcentrationPerUnit', {
        params,
        data,
        cancelTokenSource,
        showLoader,
      }),
    );
  }
  const response = await Promise.all(promises);
  // Error/cancel check
  if (response.some((r) => !r)) {
    return { count: 0, data: null };
  }
  // Combine data
  const count = response.map((r) => (r ? r.totalCount : 0)).reduce((sum, a) => sum + a, 0);
  const unsortedData = response.map((r) => r.data.data).reduce((total, res) => [...total, ...res], []);
  // Apply sort
  const didSort = !!params.$orderby;
  const isSortingDesc = didSort && params.$orderby.includes('desc');
  const sortedData = didSort
    ? unsortedData.sort((a, b) =>
        isSortingDesc
          ? b.averageConcentrations - a.averageConcentrations
          : a.averageConcentrations - b.averageConcentrations,
      )
    : unsortedData;
  // Apply skip / top
  const data = sortedData.slice(skip, skip + top);
  return { count, data };
}

export { getBatchedConcentrationData, getBatchedConcentrationDataFiltered };
